// Network lists and a task group's egress (D81, docs/12 "Network lists and egress usage"),
// written by hand until the routes reach the generated schema (S116). The API keeps lists per
// user (`ManageOwnTaskGroups`); changes reach running agents within seconds.
import { queryOptions } from "@tanstack/react-query";

import { reportAuthHint } from "../api/client";
import { requestJson } from "../api/http";
import { ApiError } from "../api/queries";

export interface UsedBy {
  task_group_id: string;
  name: string;
}

export interface NetworkList {
  id: string;
  name: string;
  description: string;
  domains: string[];
  used_by: UsedBy[];
  created_at: number;
  updated_at: number;
}

export interface ImportResult {
  added: number;
  duplicates: number;
  invalid: string[];
  list: NetworkList;
}

export interface DomainCount {
  domain: string;
  count: number;
  /** Unix ms; blocked domains only. */
  last_seen_ms?: number;
}

export interface GroupNetwork {
  days: number;
  allowed: number;
  blocked: number;
  blocked_domains: DomainCount[];
  allowed_domains: DomainCount[];
  network_list: { id: string; name: string } | null;
  share_vm_network: boolean;
}

const enc = encodeURIComponent;
type Json = Record<string, unknown>;
const obj = (x: unknown): Json => (typeof x === "object" && x !== null ? (x as Json) : {});
const str = (x: unknown): string => (typeof x === "string" ? x : "");
const num = (x: unknown): number => (typeof x === "number" && Number.isFinite(x) ? x : 0);
const strings = (x: unknown): string[] => (Array.isArray(x) ? x.filter((s): s is string => typeof s === "string") : []);

export function parseList(x: unknown): NetworkList {
  const l = obj(x);
  return {
    id: str(l.id),
    name: str(l.name),
    description: str(l.description),
    domains: strings(l.domains),
    used_by: (Array.isArray(l.used_by) ? l.used_by : []).map((u) => ({ task_group_id: str(obj(u).task_group_id), name: str(obj(u).name) })),
    created_at: num(l.created_at),
    updated_at: num(l.updated_at),
  };
}

function parseCounts(x: unknown): DomainCount[] {
  return (Array.isArray(x) ? x : []).map((d) => {
    const o = obj(d);
    return { domain: str(o.domain), count: num(o.count), ...(typeof o.last_seen_ms === "number" ? { last_seen_ms: o.last_seen_ms } : {}) };
  });
}

export function parseGroupNetwork(x: unknown): GroupNetwork {
  const n = obj(x);
  const list = obj(n.network_list);
  return {
    days: num(n.days) || 7,
    allowed: num(n.allowed),
    blocked: num(n.blocked),
    blocked_domains: parseCounts(n.blocked_domains),
    allowed_domains: parseCounts(n.allowed_domains),
    network_list: n.network_list ? { id: str(list.id), name: str(list.name) } : null,
    share_vm_network: n.share_vm_network === true,
  };
}

export const networkListsQuery = () =>
  queryOptions({
    queryKey: ["network-lists"],
    queryFn: async () => ((await requestJson("GET", "/api/network-lists")) as unknown[]).map(parseList),
  });

export const networkListQuery = (id: string) =>
  queryOptions({
    queryKey: ["network-lists", id],
    queryFn: async () => parseList(await requestJson("GET", `/api/network-lists/${enc(id)}`)),
  });

export const essentialsQuery = () =>
  queryOptions({
    queryKey: ["network-essentials"],
    queryFn: async () => strings(await requestJson("GET", "/api/network/essentials")),
    staleTime: Infinity,
  });

export interface ListBody {
  name: string;
  description: string;
}

export const createList = async (body: ListBody & { domains: string[] }) => parseList(await requestJson("POST", "/api/network-lists", body));

export const updateList = async (id: string, body: ListBody) => parseList(await requestJson("PUT", `/api/network-lists/${enc(id)}`, body));

export async function deleteList(id: string): Promise<void> {
  await requestJson("DELETE", `/api/network-lists/${enc(id)}`);
}

export const changeDomains = async (id: string, change: { add?: string[]; remove?: string[] }) =>
  parseList(await requestJson("POST", `/api/network-lists/${enc(id)}/domains`, { add: change.add ?? [], remove: change.remove ?? [] }));

/** Upload a file's text; the API parses it (one per line, `#` comments, a CSV's first column). */
export async function importDomains(id: string, text: string): Promise<ImportResult> {
  const response = await globalThis.fetch(new URL(`/api/network-lists/${enc(id)}/import`, location.origin), {
    method: "POST",
    headers: { "X-OCS-CSRF": "1", "Content-Type": "text/plain" },
    body: text,
    credentials: "same-origin",
  });
  reportAuthHint(response);
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, body);
  const r = obj(body);
  return { added: num(r.added), duplicates: num(r.duplicates), invalid: strings(r.invalid), list: parseList(r.list) };
}

export const groupNetworkQuery = (groupId: string, days: number) =>
  queryOptions({
    queryKey: ["task-groups", groupId, "network", days],
    queryFn: async () => parseGroupNetwork(await requestJson("GET", `/api/task-groups/${enc(groupId)}/network?days=${days}`)),
    refetchInterval: 30_000,
  });

/** Adds them to the group's list — every group using that list gets them too. */
export const allowDomains = async (groupId: string, domains: string[]) =>
  parseGroupNetwork(await requestJson("POST", `/api/task-groups/${enc(groupId)}/network/allow`, { domains }));
